import React from 'react'
// @src imports
import connect from 'src/redux/connect'
import { PersistState } from 'src/chrome'
import { PricingRoute } from 'src/marketing/plans/routes/PricingRoute'
import { Category, PlanFragment } from 'src/graphql'
import { PlansState } from 'src/redux/reducers/plans'
import { getPlan } from 'src/helpers'
import {
  mobilePricingTableData,
  pricingTableData,
} from 'src/redux/selectors/plans'
// relative imports
import { Plan } from '../types'
import {
  AccountStorage,
  AutomatedSendsGrid,
  BasicPlan,
  MobilePricingTable,
  PlanContentsGrid,
  WaysToSend,
} from '../components'

import styles from './plans.module.scss'
import { openApp } from 'src/helpers/appHelpers'
import { Div, Flex, PricingTile } from '@sendoutcards/quantum-design-ui'
import { planDetails } from '../constants'

type RoutesActions = {
  openCampaignStore?: () => void
}

type PlansProps = {
  upsale?: {
    title?: string
    message?: string
    highlightPlan?: PlanFragment
  }
  pricingInfo: Category[]
  mobilePricingInfo: Plan[]
  route?: PricingRoute
  screenWidth?: number
} & PlansState &
  RoutesActions

type State = {
  upgradeConfirm: boolean
  cancelConfirmation: boolean
  downgrade?: string
  selectedPlan?: Plan
  purchasing?: boolean
  isAffiliate: boolean
  join: boolean
  next: string | null
}

const initialState: State = {
  upgradeConfirm: false,
  cancelConfirmation: false,
  purchasing: false,
  isAffiliate: false,
  join: false,
  next: null,
}

class Plans extends React.Component<PlansProps, State> {
  state: State = {
    ...initialState,
  }

  handleAffiliate = () => {
    openApp('pricing', { join: '', affiliate: '' })
  }

  onCloseModal = () => {
    this.setState({
      ...initialState,
      next: this.state.next,
    })
  }

  onFinishJoin = () => {
    const { next, selectedPlan } = this.state

    this.setState({
      join: false,
    })

    if (next) {
      if (
        next === 'showPlanUpgrade' &&
        selectedPlan &&
        selectedPlan.title === 'Basic'
      ) {
        window.location.reload()
      } else {
        ;(this as any)[next]()
      }
    }
  }

  closeCancel = () => {
    this.setState({
      cancelConfirmation: false,
    })
  }

  showPlanUpgrade = (planTitle: string) => {
    const { plans } = this.props
    const { selectedPlan } = this.state

    const plan: any =
      (plans && plans.find(p => p.title === planTitle)) || selectedPlan
    // need to modify promo query param to be plan id
    openApp('', { join: '', promo: plan.id, planSelected: '' })
  }

  handleAddonPurchaseClick = () => {
    openApp()
  }

  componentWillUnmount() {
    window.localStorage.removeItem('join-page')
  }

  getPlan = (planId: string): PlanFragment | undefined =>
    getPlan(this.props.plans, planId)

  // tslint:disable-next-line: cyclomatic-complexity score 39
  render() {
    const { plans, pricingInfo, upsale } = this.props
    const upsaleTitle = upsale?.title
    const upsaleMessage = upsale?.message

    const defaultUpsale =
      'We have the perfect subscription for everyone, no matter how you choose to use SendOutCards. Start making a difference by choosing the plan that will work best for you. If you change your mind later, no worries — you can always switch plans or cancel.'

    return (
      <div>
        <div
          className={styles.plansContainer}
          style={
            !upsaleTitle || !upsaleMessage
              ? {
                  marginTop: 50,
                }
              : {}
          }
        >
          <PersistState
            name="join-page"
            data={this.state}
            onMount={data => {
              this.setState({
                ...data,
                purchasing: false,
                showResponseModal: false,
                upgradeConfirm: false,

                join: false,
              })
            }}
          />
          <div className={styles.planCopy}>
            <h4>{upsaleTitle ?? 'Upgrade your life today.'}</h4>
            <p>{upsaleMessage ?? defaultUpsale}</p>
          </div>
          <Flex
            outset="x2"
            flexWrap="wrap"
            justifyContent="space-evenly"
            alignItems="flex-start"
          >
            {pricingInfo &&
              plans
                .filter(plan => plan.title.toLowerCase() !== 'basic')
                .map(plan => {
                  const details = planDetails.find(p => p.plan === plan.title)
                  const planFeatures = details?.details.map(detail => {
                    if (detail.innerDetails) {
                      return {
                        feature: detail.detail,
                        subFeatures: detail.innerDetails.map(d => d.detail),
                        isNotIncluded: false, // todo fix this
                      }
                    }
                    return detail.detail
                  })
                  if (planFeatures && details) {
                    return (
                      <div className={styles.pricingTable}>
                        <Div
                          outset={{ horizontal: 'x1' }}
                          height="auto"
                          maxWidth="456px"
                        >
                          <PricingTile
                            featuredItemStyles={{
                              backgroundColor: 'accent',
                              textColor: 'gray',
                              accentColor: 'secondaryBrand',
                            }}
                            accentColor="secondaryBrand"
                            description={details.description}
                            title={details.plan}
                            features={planFeatures}
                            isSelected={details.isFeatured}
                            billingInterval={'Monthly'}
                            isFeatured={details.isFeatured ?? false}
                            primaryAction={{
                              text: 'Purchase Now',
                              onClick: () => this.showPlanUpgrade(details.plan),
                            }}
                          />
                        </Div>
                      </div>
                    )
                  }
                  return <> </> // todo don't return a fragment
                })}
          </Flex>
          <div
            className={`${styles.pricingTableContainer} ${styles.mobileTable}`}
          >
            {this.props.mobilePricingInfo && (
              <MobilePricingTable
                account={undefined}
                plans={this.props.mobilePricingInfo}
                currentPlan={''}
                onSelect={this.showPlanUpgrade}
                getPlan={this.getPlan}
              />
            )}
          </div>
        </div>
        <BasicPlan onSelect={this.showPlanUpgrade} />
        <WaysToSend screenWidth={this.props.screenWidth} />
        <PlanContentsGrid />
        <AutomatedSendsGrid />
        <AccountStorage screenWidth={this.props.screenWidth} />
      </div>
    )
  }
}

export default connect(state => {
  const pricingInfo = pricingTableData(
    state.plans.categories,
    state.plans.features,
    state.plans.plans,
  )
  const mobilePricingInfo = mobilePricingTableData(
    state.plans.categories,
    state.plans.features,
    state.plans.plans,
  )
  return {
    ...state.plans,
    pricingInfo,
    mobilePricingInfo,
    upsale:
      state.plans.upsaleMessage &&
      state.plans.upsaleTitle &&
      state.plans.highlightPlan
        ? {
            message: state.plans.upsaleMessage,
            title: state.plans.upsaleTitle,
            highlightPlan: state.plans.highlightPlan,
          }
        : undefined,
    screenWidth: state.window.width,
  }
}, {})(Plans)
